import { useEffect, useState } from "react";
import { Container, Grid, Title } from "@mantine/core";
import {
  IconCalendarDue, IconCircleCheck, IconList, IconStar, IconSun, IconUser,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { getTaskCounts } from "../api";
import { useStore } from "../store";
import ScopeNav from "../components/ScopeNav";
import TasksView from "./TasksView";

// Smart lists. countKey indexes the /tasks/counts payload (null = no badge for that list).
// "Assigned to me" is first + the default — it's the full personal queue, so a newly created task
// (auto-assigned to you) is always visible there. "My Day" = overdue + due today.
const SCOPES = [
  { value: "assigned", icon: IconUser, countKey: "assigned" },
  { value: "myDay", icon: IconSun, countKey: "myDay" },
  { value: "planned", icon: IconCalendarDue, countKey: "planned" },
  { value: "important", icon: IconStar, countKey: "important" },
  { value: "all", icon: IconList, countKey: null },
  { value: "completed", icon: IconCircleCheck, countKey: null },
];

export default function TasksPage() {
  const { t } = useTranslation("Veft");
  const [scope, setScope] = useState("assigned");
  const [counts, setCounts] = useState(null);
  const setTaskNavBadge = useStore((s) => s.setTaskNavBadge);

  const refreshCounts = () =>
    getTaskCounts()
      .then((c) => { setCounts(c); setTaskNavBadge(c?.navBadge ?? 0); })
      .catch(() => {});

  // Fetch counts once on mount; refreshCounts is recreated each render, so it must stay out of deps.
  useEffect(() => { refreshCounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container size="lg" py="md">
      <Title order={3} mb="md">{t("tasks_page_title")}</Title>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 3 }}>
          {/* Sidebar on desktop, a compact scrollable chip row on mobile — so the task list isn't
              buried under a full-width six-row nav block on a phone. */}
          <ScopeNav
            value={scope}
            onChange={setScope}
            scopes={SCOPES.map((s) => ({
              value: s.value,
              label: t(`tasks_list_${s.value}`),
              icon: <s.icon size={16} />,
              count: s.countKey && counts ? counts[s.countKey] : 0,
            }))}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 9 }}>
          <TasksView
            fetchParams={{ scope }}
            showLinks
            emptyMessage={t("tasks_no_match")}
            onChanged={refreshCounts}
          />
        </Grid.Col>
      </Grid>
    </Container>
  );
}
